import { Facility } from 'src/stores/FacilityStore';
import { FacilitySubscriptionData } from 'common/lib/model/facility/FacilitySubscriptionData';
import * as ROUTES from 'src/api/routes';
import request from 'src/api/utils';
import * as utils from 'src/utils';
import { CaptureAiMode } from 'common/lib/model/facility/FacilityOptions';

export interface FacilityCloneFormCandidate {
    formDefinitionId: number;
    formDefinitionName: string;
    formDefinitionTitle: string;
    formDefinitionType: string;
    schemaVersion: string;
    formDefinitionVersion: number;
    modelDefinitionId: number;
    modelDefinitionVersion: number;
    pageCount: number;
    active: boolean;
}

export interface FacilityCloneValueSetCandidate {
    valueSetId: number;
    valueSetName: string;
    valueSetDescription: string;
    valueSetType: 'STATIC' | 'DYNAMIC';
    categoryName: string;
    attributeList: string[];
    detailCount: number;
    dynamicDependencies: string[];
    active: boolean;
}

export interface FacilityCloneOptionCandidate {
    optionName: string;
    optionDisplayName: string;
    optionDescription: string;
    optionValue: string;
    inputType: 'boolean' | 'date' | 'datetime' | 'email-list' | 'text';
}

export interface FacilityCloneCandidates {
    sourceFacility: Facility & { facilityTimeZone: string };
    forms: FacilityCloneFormCandidate[];
    valueSets: FacilityCloneValueSetCandidate[];
    facilityOptions: FacilityCloneOptionCandidate[];
}

export interface CloneFacilityPayload {
    facility: Facility;
    formDefinitionIds: number[];
    valueSets: Array<{ valueSetId: number; copyDetails?: boolean }>;
    facilityOptions: Array<{ optionName: string; optionValue: string }>;
}

export async function fetchFacilities(includeInactive: boolean) {
    const facilities = await request({ ...ROUTES.GET_FACILITIES, params: { includeInactive } });
    return facilities;
}

export async function fetchFacilitiesByOrg(orgNmIntrnl: string): Promise<Facility[]> {
    const response = await request({ ...ROUTES.GET_FACILITIES_BY_ORG, pathParams: { org: orgNmIntrnl } });
    return response?.data || [];
}

export async function deactivateFacility(facId: number) {
    return await request({ ...ROUTES.DEACTIVATE_FACILITY, pathParams: { facilityId: facId } });
}

export async function reactivateFacility(facId: number) {
    return await request({
        ...ROUTES.UPDATE_FACILITY,
        pathParams: { facilityId: facId },
        data: { facId, actvInd: true },
    });
}

export async function updateFacility(facId: number, data: object) {
    const cleanedData = utils.removeNull(data);
    return await request({ ...ROUTES.UPDATE_FACILITY, pathParams: { facilityId: facId }, data: cleanedData });
}

export interface CaptureAiV2FacilityReadiness {
    currentMode: CaptureAiMode;
    canActivateV2: boolean;
    blockers: Array<{ code: string; message: string }>;
    warnings: Array<{ code: string; message: string; count: number }>;
    publishedUsableDocumentTypeCount: number;
    encounterCreationDocumentTypeCount: number;
}

export async function fetchCaptureAiV2Readiness(facilityId: number): Promise<CaptureAiV2FacilityReadiness> {
    const response = await request({
        ...ROUTES.GET_FACILITY_CAPTURE_AI_V2_READINESS,
        pathParams: { facilityId },
    });
    return response.data.readiness;
}

export async function createFacility(data: object) {
    const cleanedData = utils.removeNull(data);
    return await request({ ...ROUTES.CREATE_FACILITY, data: cleanedData });
}

export interface FacilityOption {
    optId: number;
    facId: number;
    optNm: string;
    optVal: string;
}

export async function fetchFacilityOption(facilityId: number, optionName: string): Promise<FacilityOption | null> {
    const response = await request({
        ...ROUTES.GET_FACILITY_OPTION,
        pathParams: { facilityId },
        params: { optionName },
    });
    return response.data || null;
}

export async function fetchFacilityCloneCandidates(facilityId: number): Promise<FacilityCloneCandidates> {
    const response = await request({ ...ROUTES.GET_FACILITY_CLONE_CANDIDATES, pathParams: { facilityId } });
    if (!response?.data) {
        throw new Error('Failed to fetch facility clone candidates');
    }
    return response.data;
}

export async function cloneFacility(facilityId: number, data: CloneFacilityPayload) {
    const cleanedData = utils.removeNull(data);
    return await request({ ...ROUTES.CLONE_FACILITY, pathParams: { facilityId }, data: cleanedData });
}

export async function updateFacilitySubscriptionData(
    facilityId: number,
    orgId: string | number,
    subscriptionData: FacilitySubscriptionData,
) {
    return await request({
        ...ROUTES.UPDATE_FACILITY_SUBSCRIPTION_DATA,
        pathParams: { facilityId },
        data: { orgId, subscriptionData },
    });
}
